/**
 * The values check: the lowest and highest value of each number field that
 * a table showing each value names (`TableSpec.values`), read in the
 * session after the preparation. One request reads every field the tables
 * name once the session is ready; a field a table names later is read by a
 * request of its own. What it finds reaches the compiler as data, through
 * the catalogue (`Catalogue.valueRanges`): until a field is in, its table
 * waits, and no tab book starts. Pure: `useValueCheck` sends the request.
 */
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import type {
  EngineOutputItem,
  HierarchicalDataEntry,
  RTableContent,
} from "types/engineOutput.types"
import { ref, TOTAL_TABLE, unsafeRef, unsafeText } from "../compiler/names"
import { Catalogue, findInstance, findQuestion, ValueRange } from "../types/catalogue"
import type { TableSpec } from "../types/tabSpecDocument"
import { readStatGrid } from "./statGrid"
import type { TabSpecState } from "./tabSpecStore"

/**
 * The codebook paths of the number fields `tables` show each value of, once
 * each, in table order. A field the script cannot name, or one not in the
 * loaded data, is left out: its table is skipped for that before it would
 * wait. None before data import.
 */
export const valueCheckPaths = (tables: TableSpec[], catalogue: Catalogue): string[] => {
  if (!catalogue.knownPaths) return []
  const paths = new Set<string>()
  tables.forEach((t) => {
    if (t.values !== true || t.kind !== "numeric") return
    const q = findQuestion(catalogue, t.question, t.source)
    const i = q ? findInstance(q, t.instance) : null
    if (!i || !i.inData || unsafeText(i.path) || unsafeRef(i.path)) return
    paths.add(i.path)
  })
  return [...paths]
}

/** The fields of `tables` the check has not read in this session. */
const unread = (tables: TableSpec[], catalogue: Catalogue): string[] => {
  const read = catalogue.valueRanges ?? {}
  return valueCheckPaths(tables, catalogue).filter((p) => !(p in read))
}

/** The fields the tables of the document show each value of that the check has not read in this session. */
export const unreadValuePaths = (s: Pick<TabSpecState, "document" | "catalogue">): string[] =>
  s.catalogue ? unread(s.document.tables, s.catalogue) : []

/** Whether a tab book waits for the check: an included table needs a field it has not read yet. */
export const valuesPending = (s: Pick<TabSpecState, "document" | "catalogue">): boolean =>
  s.catalogue !== null &&
  unread(
    s.document.tables.filter((t) => t.include),
    s.catalogue
  ).length > 0

/** The statistics the check asks for, read back by their names. */
const BASE_N = STATISTIC_TYPE.BASE_N
const MIN = STATISTIC_TYPE.MIN
const MAX = STATISTIC_TYPE.MAX

/** The temporary names of the check's tables, one per field, removed by the script itself. */
export const valueCheckNames = (paths: string[]): string[] => paths.map((_, i) => `__ts_vc_${i + 1}`)

/**
 * One request: per field, a DTAB of its base, lowest and highest value over
 * everyone (the Total table), its output, and its removal.
 */
export const valueCheckScript = (paths: string[]): string => {
  const names = valueCheckNames(paths)
  return paths
    .flatMap((path, i) => [
      `DTAB ${names[i]} ${TOTAL_TABLE} ${ref(path)} OPERATORS: "${BASE_N}", "${MIN}", "${MAX}"`,
      `OUTPUT ${names[i]} /T`,
      `REMOVE ${names[i]}`,
    ])
    .join("\n")
}

/** The type the codebook gives the field at `path` ("INT", "REAL" ...); null when it has none there. */
export const codebookType = (
  codebook: HierarchicalDataEntry[] | null,
  path: string
): string | null => {
  let entries: unknown = codebook
  let found: HierarchicalDataEntry | undefined
  for (const part of path.split("//")) {
    if (!Array.isArray(entries)) return null
    found = (entries as HierarchicalDataEntry[]).find((e) => e && e.label === part)
    if (!found) return null
    entries = found.value
  }
  return found ? String(found.type) : null
}

/** One field's output: its range, "empty" when its base is 0, "failed" when it holds no figures. */
const readOne = (item: EngineOutputItem | undefined, whole: boolean): ValueRange => {
  const content = item?.content as RTableContent | undefined
  if (!content || !Array.isArray(content.data)) return "failed"
  const grid = readStatGrid(content)
  // Read by name: the statistics of the one row, wherever the engine puts them.
  const stat = (name: string): number | null => {
    const c = grid.columns.findIndex((col) => col.stat === name)
    const value = c >= 0 ? grid.rows[0]?.values[c] : null
    return typeof value === "number" && Number.isFinite(value) ? value : null
  }
  const base = stat(BASE_N)
  if (base === null) return "failed"
  if (base === 0) return "empty"
  const min = stat(MIN)
  const max = stat(MAX)
  return min === null || max === null ? "failed" : { min, max, whole }
}

/**
 * What the check found of each field, from the request's outputs (one per
 * field, by position) and the codebook of the session: a field is whole
 * when the codebook calls it INT.
 */
export const readValueCheck = (
  paths: string[],
  outputs: EngineOutputItem[],
  codebook: HierarchicalDataEntry[] | null
): Record<string, ValueRange> =>
  Object.fromEntries(
    paths.map((path, i) => [path, readOne(outputs[i], codebookType(codebook, path) === "INT")])
  )
